import type { ExploratoryEffort } from './exploratory-effort.js';

/**
 * Canonical review-flavor value list — the single source of truth consumed by
 * the `review_flavor` pg enum (`packages/db/src/schema/enums.ts`), API zod
 * schemas, repository types, and the web client. Adding a new flavor here
 * requires a pg enum migration (`bun run db:generate`), a
 * `FlavorDefinition` (see `apps/api/src/adhoc-test/flavors/index.ts`) and a
 * `PLAN_PREPARERS` entry in `apps/api/src/adhoc-test/run.ts`.
 */
export const REVIEW_FLAVOR_VALUES = ['functional', 'ux', 'spec', 'fuzz'] as const;

export type ReviewFlavor = (typeof REVIEW_FLAVOR_VALUES)[number];

/** Where a run can execute. Mirrors the `kind` of the start-route target. */
export type FlavorTargetKind = 'live' | 'sandbox' | 'sandbox-v2-environment' | 'sandbox-v2';

/**
 * How a flavor treats credentials.
 * - `optional`: one credential may be chosen; none runs anonymously.
 * - `tempIdentity`: like `optional`, and may mint a throwaway identity for sign-up walkthroughs.
 * - `inventory`: the agent chooses every actor from the property's credential inventory
 *   (no single credential is picked at start; the inventory must not be empty).
 */
export type FlavorCredentialMode = 'optional' | 'tempIdentity' | 'inventory';

/**
 * The capabilities both the API (start validation, executor) and the web
 * launcher read, so the target and credential rules cannot drift between them.
 */
export const FLAVOR_CAPABILITIES: Record<
  ReviewFlavor,
  {
    supportedTargets: readonly FlavorTargetKind[];
    credentials: FlavorCredentialMode;
    /** Flavors with an effort choice (see `ExploratoryEffort`) preselect `default`; `null` = no effort. */
    effort: { default: ExploratoryEffort } | null;
  }
> = {
  functional: {
    supportedTargets: ['live', 'sandbox', 'sandbox-v2-environment', 'sandbox-v2'],
    credentials: 'optional',
    effort: null,
  },
  ux: { supportedTargets: ['live'], credentials: 'tempIdentity', effort: null },
  spec: { supportedTargets: ['live'], credentials: 'inventory', effort: null },
  // Fuzzing is destructive, so it runs only in private sandbox v2 copies, never live or on sandbox v1.
  fuzz: {
    supportedTargets: ['sandbox-v2-environment', 'sandbox-v2'],
    credentials: 'optional',
    effort: { default: 'standard' },
  },
};
